import i18n from '@/utils/i18n';
import { createPriceValidationMessage } from '@/domain/pricing/utils/validation';
import { formatCurrency } from '@/utils/money';
import {
  formatGrossProfitForSave,
  formatPriceForSave,
} from '@/utils/price-editing';
import {
  orderEntryCollection,
  orderGuideItemCollection,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/collections/order-entry-collection';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { PriceEditType, SalesRepOverrideExpirationSource } from '@/types';
import { SalesRepConfig } from '@domain/configuration/types';
import { SalesRepOverrideMode } from '@/enums';
import { transformOrderEntryItem } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { transformOrderEntryItemToPriceEditorConfig } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-item-to-price-editor-config-transformer';
import { useCallback } from 'react';
import { useGrossProfitOverrideMutator } from '@/domain/pricing/blocs/mutators/use-gross-profit-override-mutator';
import { usePriceOverrideMutator } from '@/domain/pricing/blocs/mutators/use-price-override-mutator';
import type { SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment } from '@/types';

const OVERRIDE_MODE_TO_PRICE_EDIT_TYPE: Record<
  SalesRepOverrideMode,
  PriceEditType | null
> = {
  [SalesRepOverrideMode.DISABLED]: null,
  [SalesRepOverrideMode.GROSS_PROFIT]: PriceEditType.GrossProfit,
  [SalesRepOverrideMode.PRICE]: PriceEditType.Price,
};

interface UsePriceEditSaveParams {
  deliveryDate: string;
  onSaveError: (message: string) => void;
  overrideMode: SalesRepOverrideMode;
  restaurantUUID: string;
  salesRepConfig: SalesRepConfig | null;
  supplierUUID: string;
}

/**
 * Writes an updated raw item to `orderGuideItemCollection` and derives the
 * matching display row via `transformOrderEntryItem`. Shared by the optimistic
 * and post-save writes so display formatting stays consistent across the flow.
 */
function applyRawUpdate(
  variantPackUUID: string,
  updatedRawItem: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment,
): void {
  // don't keep server formatted strings for the old price
  const rawItem = updatedRawItem.variant_pack_info
    ? {
        ...updatedRawItem,
        variant_pack_info: {
          ...updatedRawItem.variant_pack_info,
          gross_profit_amount_display: null,
          pack_price_display: null,
          unit_price_display: null,
        },
      }
    : updatedRawItem;
  orderGuideItemCollection.delete([variantPackUUID]);
  orderGuideItemCollection.insert([rawItem]);

  // Upsert the pricing field changes
  const currentDisplayRow = orderEntryCollection.get(variantPackUUID);
  const freshRow = transformOrderEntryItem(
    rawItem,
    currentDisplayRow ? currentDisplayRow.sortRank : orderEntryCollection.size,
  );
  if (currentDisplayRow) {
    orderEntryCollection.delete([variantPackUUID]);
  }
  orderEntryCollection.insert([
    currentDisplayRow
      ? {
          ...currentDisplayRow,
          grossProfitAmountInMicros: freshRow.grossProfitAmountInMicros,
          grossProfitDollarDisplayString:
            freshRow.grossProfitDollarDisplayString,
          grossProfitPercentage: freshRow.grossProfitPercentage,
          grossProfitPercentageDisplayString:
            freshRow.grossProfitPercentageDisplayString,
          priceDisplayString: freshRow.priceDisplayString,
          priceMicros: freshRow.priceMicros,
          priceOverrideInfo: freshRow.priceOverrideInfo,
          unitPriceInfo: freshRow.unitPriceInfo,
        }
      : freshRow,
  ]);
}

/**
 * Shared save hook for price and GP% cells. Handles validation, optimistic
 * collection updates, mutation dispatch on blur, and rollback on failure.
 * After a successful save, rebases the editor's config so that a subsequent
 * `clearEdits()` reverts to the last save rather than the original server value.
 *
 * @param params.deliveryDate - Fulfillment delivery date (ISO string)
 * @param params.overrideMode - Supplier's override mode (PRICE or GROSS_PROFIT)
 * @param params.restaurantUUID - Restaurant UUID
 * @param params.salesRepConfig - Supplier's sales rep config for editor rebasing
 * @param params.supplierUUID - Supplier UUID
 *
 * @returns void — `handleSave(editor, variantPackUUID)` is an async callback to invoke on blur
 *
 * @example
 * ```typescript
 * const { handleSave } = usePriceEditSave({
 *   deliveryDate,
 *   overrideMode: SalesRepOverrideMode.PRICE,
 *   restaurantUUID,
 *   salesRepConfig,
 *   supplierUUID,
 * });
 *
 * const handleBlur = useCallback(() => {
 *   if (editor) void handleSave(editor, variantPackUUID);
 * }, [editor, handleSave, variantPackUUID]);
 * ```
 */
export const usePriceEditSave = ({
  deliveryDate,
  onSaveError,
  overrideMode,
  restaurantUUID,
  salesRepConfig,
  supplierUUID,
}: UsePriceEditSaveParams) => {
  const { commitPrice } = usePriceOverrideMutator();
  const { commitGrossProfit } = useGrossProfitOverrideMutator();

  const handleSave = useCallback(
    async (editor: PriceEditor, variantPackUUID: string): Promise<boolean> => {
      const editorState = editor.getState();

      // No changes to commit.
      if (!editorState.isDirty && !editor.hasExpirationChange()) {
        return false;
      }

      // missing required params
      if (!deliveryDate || !restaurantUUID || !supplierUUID) {
        return false;
      }

      // Cells validate on blur and show inline errors, but we guard here too
      // to prevent saving invalid state if save is triggered programmatically.
      // Revert the user's edit and show the validation error.
      const validation = editor.validate(editorState);
      if (!validation.valid) {
        editor.clearEdits();
        if (validation.reason) {
          const validationMessage = createPriceValidationMessage(
            validation.reason,
            validation.bound,
          );
          if (validationMessage) {
            onSaveError(validationMessage);
          }
        }
        return false;
      }

      const isGrossProfitMode =
        overrideMode === SalesRepOverrideMode.GROSS_PROFIT;

      // Check for deletion
      const grossProfitForSave = formatGrossProfitForSave(
        editorState.salesRepGrossProfitPercent,
      );
      const priceForSave = formatPriceForSave(
        editorState.salesRepPriceInMicros,
      );
      const isOverrideDeletion = isGrossProfitMode
        ? grossProfitForSave === null
        : priceForSave === null;

      // Delete any set expiration date if the override is being deleted
      if (isOverrideDeletion) {
        editor.setExpirationDate(null);
      }

      const expirationDate =
        salesRepConfig?.can_set_manual_override_expiration &&
        !isOverrideDeletion
          ? editorState.expirationDate
          : null;

      editor.setSaveInProgress(true);

      // Snapshot current collection state for rollback
      const previousDisplayRow =
        orderEntryCollection.get(variantPackUUID) ?? null;
      const previousRawItem =
        orderGuideItemCollection.get(variantPackUUID) ?? null;
      const previousVpInfo = previousRawItem?.variant_pack_info;
      const packPriceInMicros = editor.getPackPriceInMicros();

      // Optimistic update: write editor values to the raw item; the transformer
      // derives the matching display strings via applyRawUpdate.
      if (previousRawItem && previousVpInfo) {
        applyRawUpdate(variantPackUUID, {
          ...previousRawItem,
          variant_pack_info: {
            ...previousVpInfo,
            gross_profit_in_percentage: editorState.salesRepGrossProfitPercent,
            // Only write pack/unit price when setting a value; on clear (null)
            // keep the server default so it can be restored.
            ...(packPriceInMicros !== null
              ? { pack_price_in_micros: packPriceInMicros }
              : {}),
            ...(editorState.salesRepPriceInMicros !== null
              ? { unit_price_in_micros: editorState.salesRepPriceInMicros }
              : {}),
            price_edit_info: {
              ...previousVpInfo.price_edit_info,
              gross_profit_override_in_percentage:
                editorState.salesRepGrossProfitPercent,
              price_override_in_micros: editorState.salesRepPriceInMicros,
              ...(salesRepConfig?.can_set_manual_override_expiration
                ? {
                    expiration_source: expirationDate
                      ? SalesRepOverrideExpirationSource.SalesRepTemporary
                      : null,
                    expires_at: expirationDate,
                  }
                : {}),
            },
          },
        });

        // Overwrite GP$ with the editor's portion-aware value: the transformer
        // always derives pack-level GP$, which is wrong in UNIT portion display.
        const editorGpInMicros = editor.getGrossProfitInMicros();
        const optimisticDisplayRow = orderEntryCollection.get(variantPackUUID);
        if (optimisticDisplayRow) {
          orderEntryCollection.delete([variantPackUUID]);
          orderEntryCollection.insert([
            {
              ...optimisticDisplayRow,
              grossProfitAmountInMicros: editorGpInMicros,
              grossProfitDollarDisplayString:
                editorGpInMicros !== null
                  ? formatCurrency(editorGpInMicros, previousVpInfo.currency)
                  : '',
            },
          ]);
        }
      }

      // Dispatch mutation based on override mode
      const mutationParams = {
        deliveryDate,
        expirationDate,
        restaurantUUID,
        supplierUUID,
        variantPackUUID,
      };

      const result = isGrossProfitMode
        ? await commitGrossProfit({
            ...mutationParams,
            grossProfitInPercentage: grossProfitForSave,
          })
        : await commitPrice({
            ...mutationParams,
            priceInMicros: priceForSave,
          });

      if (!result) {
        // Clear saveInProgress before clearEdits — PriceEditor.clearEdits()
        // silently returns early when saveInProgress is true, which would
        // prevent the editor state from reverting to pre-edit values.
        editor.setSaveInProgress(false);
        editor.clearEdits();

        // Notify user of save failure with item name
        const itemName =
          orderGuideItemCollection.get(variantPackUUID)?.item_info?.name ?? '';
        onSaveError(
          i18n.t('sales_portal.order_entry.table.price_save_failed', {
            itemName,
          }),
        );

        // Rollback collections to pre-edit state by restoring snapshots.
        if (previousDisplayRow) {
          orderEntryCollection.delete([variantPackUUID]);
          orderEntryCollection.insert([previousDisplayRow]);
        }
        if (previousRawItem) {
          orderGuideItemCollection.delete([variantPackUUID]);
          orderGuideItemCollection.insert([previousRawItem]);
        }
        return false;
      }

      editor.markAsSaved();

      // Sync the raw item with the server's authoritative response.
      const effectiveUnitPrice =
        result.salesRepPriceInMicros ?? result.priceInMicros;
      const serverRawItem = orderGuideItemCollection.get(variantPackUUID);
      const serverVpInfo = serverRawItem?.variant_pack_info;

      if (serverRawItem && serverVpInfo) {
        // Show the override indicator unless the saved price matches the catalog
        // default; snapshot pre-override defaults so a later clear can restore them.
        const defaultUnitPriceMicros =
          serverVpInfo.default_unit_price_in_micros;
        const isOriginalPrice =
          defaultUnitPriceMicros !== null &&
          result.salesRepPriceInMicros === defaultUnitPriceMicros;
        const hasOverride =
          !isOriginalPrice &&
          (result.salesRepPriceInMicros !== null ||
            result.salesRepGrossProfitInPercentage !== null);

        let activeOverride: PriceEditType | null = null;
        let defaultPackPrice = serverVpInfo.default_pack_price_in_micros;
        let defaultUnitPrice = serverVpInfo.default_unit_price_in_micros;

        if (hasOverride) {
          activeOverride = OVERRIDE_MODE_TO_PRICE_EDIT_TYPE[overrideMode];
          defaultPackPrice ??= previousVpInfo?.pack_price_in_micros;
          defaultUnitPrice ??= previousVpInfo?.unit_price_in_micros;
        }

        // Server-authoritative variant pack, minus the derived pack price + GP%
        // (filled in below from a PriceEditor so pricing math lives in one place).
        const serverVariantPackInfo = {
          ...serverVpInfo,
          default_pack_price_in_micros: defaultPackPrice,
          default_unit_price_in_micros: defaultUnitPrice,
          price_edit_info: {
            ...serverVpInfo.price_edit_info,
            active_override: activeOverride,
            gross_profit_override_in_percentage:
              result.salesRepGrossProfitInPercentage,
            price_edited_by_user_name: result.updatedByName,
            price_override_in_micros: result.salesRepPriceInMicros,
          },
          unit_price_in_micros: effectiveUnitPrice,
        };

        // Derive pack price + GP% via a PriceEditor on the server values so pricing math lives in one place.
        const serverConfig = salesRepConfig
          ? transformOrderEntryItemToPriceEditorConfig(
              { ...serverRawItem, variant_pack_info: serverVariantPackInfo },
              salesRepConfig,
            )
          : null;
        const serverEditor = serverConfig
          ? new PriceEditor(serverConfig)
          : null;

        applyRawUpdate(variantPackUUID, {
          ...serverRawItem,
          variant_pack_info: {
            ...serverVariantPackInfo,
            gross_profit_in_percentage:
              serverEditor?.getGrossProfitPercent() ??
              serverVpInfo.gross_profit_in_percentage ??
              null,
            pack_price_in_micros:
              serverEditor?.getPackPriceInMicros() ??
              serverVpInfo.pack_price_in_micros ??
              null,
          },
        });

        // Rebase the editor's config so clearEdits() reverts to the saved values.
        if (serverConfig) {
          editor.updateConfig(serverConfig);
        }
      }

      editor.setSaveInProgress(false);
      return true;
    },
    [
      commitGrossProfit,
      commitPrice,
      deliveryDate,
      onSaveError,
      overrideMode,
      restaurantUUID,
      salesRepConfig,
      supplierUUID,
    ],
  );

  return { handleSave };
};
